/**
 * Modello unificato dei media (immagini) di attività e itinerari.
 *
 * Storicamente `itinerary_activities.media` e `activity_library.media` erano
 * array di stringhe (URL nudi). Questo faceva perdere la provenienza dell'immagine
 * e, per le foto Unsplash, i dati di attribuzione richiesti dalle
 * Unsplash API Guidelines (nome fotografo + link al profilo + link alla foto).
 *
 * Il nuovo formato persistito è un array di `MediaItem`. La lettura resta
 * retrocompatibile: `normalizeMedia()` accetta indifferentemente stringhe legacy
 * e oggetti nuovi, quindi NON serve migrare i dati esistenti — le righe si
 * convertono naturalmente al primo salvataggio.
 */

export type MediaProvider = 'unsplash' | 'ratehawk' | 'storage' | 'external';

/**
 * Dati di attribuzione Unsplash. Assenti sulle foto Unsplash legacy (solo URL).
 * Dichiarato come `type` e non `interface`: solo i type alias ottengono la index
 * signature implicita che li rende assegnabili a `Json` di Supabase, ed è così che
 * MediaItem finisce nei payload delle RPC senza cast.
 */
export type UnsplashAttributionData = {
  /** id della foto nelle API Unsplash (non ricavabile dall'URL). */
  photoId: string;
  /** `links.html` — pagina pubblica della foto su unsplash.com. */
  photoUrl: string;
  photographerName: string;
  /** `user.links.html` — profilo del fotografo su unsplash.com. */
  photographerUrl: string;
  /**
   * `links.download_location` — va pingato quando l'utente seleziona la foto.
   * Vedi trackUnsplashDownload() in services/unsplash.service.ts.
   */
  downloadLocation?: string;
};

/** Vedi la nota su UnsplashAttributionData per il perché di `type` invece di `interface`. */
export type MediaItem = {
  url: string;
  provider: MediaProvider;
  /** Testo alternativo (da Unsplash description/alt_description quando disponibile). */
  alt?: string | null;
  /** Valorizzato solo quando `provider === 'unsplash'`. */
  unsplash?: UnsplashAttributionData;
};

const UNSPLASH_HOSTS = ['images.unsplash.com', 'unsplash.com'];
const RATEHAWK_HOSTS = ['worldota.net', 'ratehawk.com'];

/**
 * Deduce il provider da un URL nudo. Usato solo per i dati legacy:
 * per le immagini nuove il provider è noto al momento della selezione.
 */
function inferProviderFromUrl(url: string): MediaProvider {
  if (!url) return 'external';
  if (url.startsWith('data:')) return 'storage';
  if (url.includes('/storage/v1/object/public/')) return 'storage';
  if (UNSPLASH_HOSTS.some((h) => url.includes(h))) return 'unsplash';
  if (RATEHAWK_HOSTS.some((h) => url.includes(h))) return 'ratehawk';
  return 'external';
}

/** Costruisce un MediaItem da un URL nudo (upload, URL manuale, provider terzi). */
export function toMediaItem(url: string, provider?: MediaProvider): MediaItem {
  return { url, provider: provider ?? inferProviderFromUrl(url) };
}

/**
 * Normalizza un singolo elemento in MediaItem.
 * Accetta `unknown` perché la sorgente è quasi sempre un `Json` di Supabase:
 * tipizzarlo più stretto costringerebbe ogni chiamante a un cast.
 */
export function normalizeMediaItem(value: unknown): MediaItem | null {
  if (!value) return null;

  if (typeof value === 'string') {
    return value.trim() ? toMediaItem(value) : null;
  }

  if (typeof value !== 'object') return null;

  const item = value as Partial<MediaItem>;
  if (typeof item.url !== 'string' || !item.url.trim()) return null;

  return {
    url: item.url,
    provider: item.provider ?? inferProviderFromUrl(item.url),
    alt: item.alt ?? null,
    ...(item.unsplash ? { unsplash: item.unsplash } : {}),
  };
}

/**
 * Normalizza il campo `media` letto dal DB (JSONB) o da un form.
 * Tollera: null, jsonb `null`, array misto stringhe/oggetti, valori sporchi.
 */
export function normalizeMedia(value: unknown): MediaItem[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(normalizeMediaItem)
    .filter((item): item is MediaItem => item !== null);
}

/** Estrae i soli URL — per PDF, cache offline, preload e copie storage. */
export function mediaUrls(value: unknown): string[] {
  return normalizeMedia(value).map((item) => item.url);
}

/**
 * Applica una trasformazione agli URL preservando provenienza e attribuzione.
 * Usato per risolvere i placeholder `{size}` degli URL Worldota/RateHawk.
 */
export function mapMediaUrls(value: unknown, transform: (url: string) => string): MediaItem[] {
  return normalizeMedia(value).map((item) => ({ ...item, url: transform(item.url) }));
}

/** True se l'item richiede l'attribuzione Unsplash completa. */
export function hasUnsplashAttribution(
  item: MediaItem | null | undefined
): item is MediaItem & { unsplash: UnsplashAttributionData } {
  return !!item && item.provider === 'unsplash' && !!item.unsplash?.photographerName;
}

/**
 * Le Unsplash API Guidelines richiedono che ogni link verso unsplash.com
 * (profilo fotografo, pagina foto, homepage) porti i parametri di referral.
 */
const UNSPLASH_APP_NAME = 'Place2Be';
export const UNSPLASH_HOME_URL = 'https://unsplash.com';

export function withUnsplashUtm(url: string): string {
  if (!url) return UNSPLASH_HOME_URL;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}utm_source=${UNSPLASH_APP_NAME}&utm_medium=referral`;
}
